import { useLazyQuery } from "@apollo/client/react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useDisplayLocale } from "~/common/functions/useDisplayLocale";
import {
  SearchGeoItemsInAreaDocument,
  type SearchGeoItemsInAreaQuery,
  type SearchGeoItemsInAreaQueryVariables,
} from "~/features/recordGeoItems/gqls";
import { geoItemToLocation } from "~/features/recordGeoItems/utils/geoItemToLocation";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";
import type { Location, ViewportType } from "~/lib/map/types";
import { getGeometryFromViewport, isCoordsInGeometry } from "~/lib/map/utils";

interface UseInViewportLocationsProps {
  isActive: boolean;
  viewport: ViewportType | null;
  onLocationsUpdate: (locations: Location[]) => void;
}

const SEARCH_PAGE_SIZE = 500;

export function useInViewportLocations({ isActive, viewport, onLocationsUpdate }: UseInViewportLocationsProps) {
  const { t } = useTranslation();
  const locale = useDisplayLocale();
  const { tenantId } = useTenant();
  const [searchGeoItems] = useLazyQuery<SearchGeoItemsInAreaQuery, SearchGeoItemsInAreaQueryVariables>(
    SearchGeoItemsInAreaDocument,
  );

  const viewportGeometry = useMemo(() => (viewport ? getGeometryFromViewport(viewport) : null), [viewport]);

  const updateLocations = useCallback(async () => {
    if (!isActive || !viewport || !viewportGeometry || !tenantId) return;
    try {
      const items: SearchGeoItemsInAreaQuery["cnutSearchGeoItems"]["items"] = [];
      let offset = 0;
      let total = 0;

      do {
        const { data } = await searchGeoItems({
          variables: {
            input: {
              tenantId,
              area: {
                mode: "intersects",
                geometry: viewportGeometry,
              },
              limit: SEARCH_PAGE_SIZE,
              offset,
            },
          },
        });
        const page = data?.cnutSearchGeoItems;
        if (!page) break;

        items.push(...page.items);
        total = page.total;
        offset += page.items.length;
        if (page.items.length === 0) break;
      } while (offset < total);

      onLocationsUpdate(
        items
          .filter(({ coords }) => coords !== null)
          .map((geo) => geoItemToLocation(geo, locale))
          .filter(({ coords }) => isCoordsInGeometry(coords, viewportGeometry)),
      );
    } catch (e) {
      if (e instanceof Error && e.name !== "AbortError") toast.error(translateError(t, e));
    }
  }, [isActive, viewport, viewportGeometry, searchGeoItems, onLocationsUpdate, t, tenantId, locale]);

  return { updateLocations };
}
